import { logger } from './logger';
import { loggerConfig } from './loggerConfig';
import { LogLevel } from './logger';

interface PantherLogConsole {
  enable: () => void;
  disable: () => void;
  setLevel: (level: keyof typeof LogLevel) => void;
  setComponentLevel: (component: string, level: keyof typeof LogLevel) => void;
  enableComponent: (component: string) => void;
  disableComponent: (component: string) => void;
  setPrettyPrint: (enabled: boolean) => void;
  getConfig: () => void;
  listComponents: () => void;
  reset: () => void;
  export: () => void;
  clear: () => void;
  help: () => void;
}

function initializeConsoleInterface(): void {
  const pantherLog: PantherLogConsole = {
    enable: () => {
      loggerConfig.enable();
    },

    disable: () => {
      loggerConfig.disable();
    },

    setLevel: (level: keyof typeof LogLevel) => {
      if (!(level in LogLevel)) {
        console.error(
          `Invalid log level: ${level}. Valid levels: DEBUG, INFO, WARN, ERROR, NONE`
        );
        return;
      }
      loggerConfig.setLevel(level);
    },

    setComponentLevel: (component: string, level: keyof typeof LogLevel) => {
      if (!(level in LogLevel)) {
        console.error(
          `Invalid log level: ${level}. Valid levels: DEBUG, INFO, WARN, ERROR, NONE`
        );
        return;
      }
      loggerConfig.setComponentLevel(component, level);
    },

    enableComponent: (component: string) => {
      loggerConfig.enableComponent(component);
    },

    disableComponent: (component: string) => {
      loggerConfig.disableComponent(component);
    },

    setPrettyPrint: (enabled: boolean) => {
      loggerConfig.setPrettyPrint(enabled);
    },

    getConfig: () => {
      const config = loggerConfig.getConfig();
      console.log('%c[Logger Configuration]', 'color: #8B5CF6; font-weight: bold; font-size: 14px');
      console.table({
        Enabled: config.enabled,
        'Global Level': LogLevel[config.globalLevel],
        'Pretty Print': config.prettyPrint,
        'Disabled Components': config.disabledComponents.join(', ') || 'None',
      });
      if (Object.keys(config.componentLevels).length > 0) {
        console.log('%cComponent Levels:', 'color: #8B5CF6; font-weight: bold');
        console.table(
          Object.entries(config.componentLevels).reduce((acc, [component, level]) => {
            acc[component] = LogLevel[level];
            return acc;
          }, {} as Record<string, string>)
        );
      }
    },

    listComponents: () => {
      const components = loggerConfig.listComponents();
      console.log('%c[Registered Components]', 'color: #8B5CF6; font-weight: bold; font-size: 14px');
      if (components.length === 0) {
        console.log('No components registered yet');
      } else {
        console.table(components.map(c => ({ Component: c })));
      }
    },

    reset: () => {
      loggerConfig.reset();
    },

    export: () => {
      logger.exportLogs();
      console.log(
        '%c[Logger] Logs exported to file',
        'color: #10B981; font-weight: bold'
      );
    },

    clear: () => {
      logger.clearBuffer();
      console.log(
        '%c[Logger] Log buffer cleared',
        'color: #F59E0B; font-weight: bold'
      );
    },

    help: () => {
      console.log(
        '%c╔══════════════════════════════════════════════════════════════════╗\n' +
        '║           PantherAccess Logger - Console Commands               ║\n' +
        '╚══════════════════════════════════════════════════════════════════╝',
        'color: #8B5CF6; font-weight: bold; font-family: monospace'
      );

      const commands = [
        ['__PANTHER_LOG__.enable()', 'Enable all logging'],
        ['__PANTHER_LOG__.disable()', 'Disable all logging'],
        ['__PANTHER_LOG__.setLevel(level)', 'Set global log level (DEBUG|INFO|WARN|ERROR|NONE)'],
        ['__PANTHER_LOG__.setComponentLevel(component, level)', 'Set component-specific log level'],
        ['__PANTHER_LOG__.enableComponent(component)', 'Enable logging for specific component'],
        ['__PANTHER_LOG__.disableComponent(component)', 'Disable logging for specific component'],
        ['__PANTHER_LOG__.setPrettyPrint(enabled)', 'Toggle pretty print mode (true|false)'],
        ['__PANTHER_LOG__.getConfig()', 'Display current configuration'],
        ['__PANTHER_LOG__.listComponents()', 'List all registered components'],
        ['__PANTHER_LOG__.reset()', 'Reset configuration to defaults'],
        ['__PANTHER_LOG__.export()', 'Export logs to JSON file'],
        ['__PANTHER_LOG__.clear()', 'Clear log buffer'],
        ['__PANTHER_LOG__.help()', 'Show this help message'],
      ];

      console.log('%cCommands:', 'color: #3B82F6; font-weight: bold; font-size: 12px');
      console.table(
        commands.map(([command, description]) => ({
          Command: command,
          Description: description,
        }))
      );

      console.log(
        '%cExamples:',
        'color: #10B981; font-weight: bold; font-size: 12px'
      );
      console.log(`
  // Enable debug logging
  __PANTHER_LOG__.setLevel('DEBUG')

  // Enable only API logging
  __PANTHER_LOG__.disableComponent('navigation')
  __PANTHER_LOG__.enableComponent('api')

  // Raw JSON output (no pretty print)
  __PANTHER_LOG__.setPrettyPrint(false)

  // Export logs
  __PANTHER_LOG__.export()
      `);
    },
  };

  (window as any).__PANTHER_LOG__ = pantherLog;

  // Silent initialization - no banner
  console.log('%c[PantherAccess] Logger initialized. Type __PANTHER_LOG__.help() for commands', 'color: #8B5CF6; font-size: 11px');
}

export { initializeConsoleInterface };
